import React from 'react';
import { Modal } from 'react-bootstrap';
import styles from './css/index.module.css';
import { Edit16Regular, Dismiss16Regular } from '@fluentui/react-icons';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { PatchProjectData } from '../../redux/projectDataSlice';

const ExternalVDRModal = ({ showModal, setShowModal }) => {
    const { selectedProject } = useSelector(state => state?.projectData);
    const { user } = useSelector(state => state?.user);
    const [buttonLoading, setButtonLoading] = React.useState(false);
    const [isEditing, setIsEditing] = React.useState(false);
    const dispatch = useDispatch();

    const [formData, setFormData] = React.useState({
        vdr_link: selectedProject?.external_vdr?.vdr_link || '',
        vdr_description: selectedProject?.external_vdr?.vdr_description || '',
    });

    const hasExistingVDR = selectedProject?.external_vdr?.vdr_link;

    const getShortUrl = url => {
        try {
            const parsed = new URL(url);
            // Show protocol + host (e.g., https://website.com or http://www.website.com)
            return `${parsed.protocol}//${parsed.host}`;
        } catch {
            // If URL constructor fails (maybe missing protocol), just show the original or up to first slash
            const match = url.match(/^(https?:\/\/)?[^\/]+/);
            return match ? match[0] : url;
        }
    };

    const handleClose = () => {
        setShowModal(false);
        setIsEditing(false);
        setFormData({
            vdr_link: selectedProject?.external_vdr?.vdr_link || '',
            vdr_description: selectedProject?.external_vdr?.vdr_description || '',
        });
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleSubmit = async () => {
        if (!formData.vdr_link.trim()) {
            toast.error('Please enter a valid VDR link');
            return;
        }

        setButtonLoading(true);

        try {
            const externalVdrData = {
                external_vdr: {
                    vdr_link: formData.vdr_link,
                    vdr_description: formData.vdr_description,
                },
            };

            await dispatch(
                PatchProjectData({
                    data: externalVdrData,
                    id: selectedProject?.id,
                }),
            );

            toast.success('External VDR link updated successfully');
            handleClose();
        } catch (error) {
            console.error('Error updating external VDR:', error);
            toast.error('Failed to update external VDR link');
        } finally {
            setButtonLoading(false);
        }
    };

    return (
        <Modal show={showModal} onHide={handleClose} centered>
            <Modal.Body style={{ padding: '24px' }}>
                <Modal.Title className={styles.sidebarTitle}>External VDR Link</Modal.Title>
                {hasExistingVDR && !isEditing ? (
                    // Read-only view for existing VDR
                    <>
                        <div className="mt-3">
                            <label className={styles.modalLabel}>Link to external VDR</label>
                            <div className="mb-3">
                                <a href={selectedProject?.external_vdr?.vdr_link} className={styles.modalVdrLink} target="_blank" rel="noopener noreferrer">
                                    {getShortUrl(selectedProject?.external_vdr?.vdr_link)}
                                </a>
                            </div>
                        </div>

                        {selectedProject?.external_vdr?.vdr_description && (
                            <div className="mb-5">
                                <label className={styles.modalLabel}>Notes / Access Info</label>
                                <div className={styles.vdrDescription}>{selectedProject?.external_vdr?.vdr_description}</div>
                            </div>
                        )}
                    </>
                ) : (
                    // Edit view (for new VDR or editing existing)
                    <>
                        <div className="mt-3">
                            <label className={styles.fieldLabel}>Link to external VDR</label>
                            <input
                                value={formData.vdr_link}
                                onChange={e => setFormData({ ...formData, vdr_link: e.target.value })}
                                className={styles.inputField + ' form-control mb-3'}
                                placeholder="https://example-vdr.com/folder123"
                            />
                        </div>

                        <div className="mb-5">
                            <label className={styles.fieldLabel}>Notes / Access Info</label>
                            <textarea
                                value={formData.vdr_description}
                                onChange={e => setFormData({ ...formData, vdr_description: e.target.value })}
                                className={styles.inputField + ' form-control'}
                                rows="4"
                                placeholder="Add login credentials, or comments here. (ex. username: user@domain.com, password: ********)"
                            />
                        </div>
                    </>
                )}
                {hasExistingVDR && !isEditing ? (
                    // Read-only view buttons
                    <div className="d-flex align-items-center justify-content-between">
                        <span onClick={handleEdit} className={styles.modalButtonText}>
                            <Edit16Regular className="me-1" /> Edit Link
                        </span>
                        <span onClick={handleClose} className={styles.modalButtonText}>
                            <Dismiss16Regular className="me-1" /> Cancel
                        </span>
                    </div>
                ) : (
                    // Edit view button
                    <button onClick={handleSubmit} disabled={buttonLoading || !formData.vdr_link.trim()} className={styles.saveButton}>
                        {buttonLoading ? 'Updating...' : hasExistingVDR ? 'Update Link' : 'Add Link'}
                    </button>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default ExternalVDRModal;
